'use client';

import { AccountPackageApp, fetchPlans, unsubscribe, updatePackage } from '@/api/billing';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNotificationChannel } from '@/components/NotificationChannelContext';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Plan, PlanType } from '@/types/plans';
import { useCurrentApp } from '@/stores/userDataStore';

const calculateRemainingCredits = ({
  subscriptionCredits,
  extraCredits,
}: {
  subscriptionCredits: number | null;
  extraCredits: number | null;
}) => {
  const total = (subscriptionCredits || 0) + (extraCredits || 0);
  return Math.max(total, 0);
};

const useBilling = () => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [currentPlanId, setCurrentPlanId] = useState<string | null>(null);
  const [subscriptionCredits, setSubscriptionCredits] = useState<number | null>(null);
  const [extraCredits, setExtraCredits] = useState<number | null>(null);
  const notificationChannel = useNotificationChannel();
  const subscriptionRef = useRef<{
    unsubscribe: () => void;
  } | null>(null);

  const { appKey, isLoggedIn, isLoading: isLoadingUser } = useCurrentApp();

  useEffect(() => {
    if (!appKey) return;

    subscriptionRef.current = {
      unsubscribe: notificationChannel.subscribe('account-package-updated', (data) => {
        if (data.app !== AccountPackageApp.studio) return;
        setCurrentPlanId(data.packageId);
        setIsLoading(false);
        if (data.subscriptionCredits !== undefined) {
          setSubscriptionCredits(data.subscriptionCredits);
        }
        if (data.extraCredits !== undefined) {
          setExtraCredits(data.extraCredits);
        }
      }),
    };

    return () => {
      subscriptionRef.current?.unsubscribe();
      subscriptionRef.current = null;
    };
  }, [appKey, notificationChannel]);

  const {
    data: billingData,
    isLoading: isBillingLoading,
    isError: isBillingError,
    isPending: isBillingPending,
  } = useQuery({
    queryKey: ['billing', appKey],
    queryFn: () => fetchPlans({ appKey }),
    enabled: !!appKey && isLoggedIn,
  });

  useEffect(() => {
    if (billingData) {
      setCurrentPlanId(billingData.appAccountPackage?.packageId || null);
      setSubscriptionCredits(billingData.appAccountPackage?.subscriptionCredits || 0);
      setExtraCredits(billingData.appAccountPackage?.extraCredits || 0);
    }
  }, [billingData]);

  useEffect(() => {
    const remainingCredits = calculateRemainingCredits({
      subscriptionCredits,
      extraCredits,
    });

    console.log(`Credits: 
      Subscription Credits: ${subscriptionCredits}, 
      Extra Credits: ${extraCredits}, 
      Remaining Credits: ${remainingCredits}`);
  }, [subscriptionCredits, extraCredits]);

  const changePackage = useCallback(
    async (plan: Plan) => {
      if (isLoading || !appKey) {
        return;
      }
      setIsLoading(true);
      try {
        if (plan.type === 'free') {
          await unsubscribe({ appKey });
        } else {
          const response = await updatePackage({ appKey, packageId: plan.id });
          if (response.link) {
            window.open(response.link, '_blank');
          }
        }
      } catch (err) {
        console.error('Error updating package:', err);
        setIsLoading(false);
      }
    },
    [appKey, isLoading]
  );

  const plans = useMemo(() => billingData?.packages || [], [billingData?.packages]);

  const currentPlan = useMemo(() => {
    if (!plans.length) return null;
    return plans.find((plan) => plan.id === currentPlanId);
  }, [plans, currentPlanId]);

  const isProPlan = useMemo(() => {
    if (!currentPlan) return null;

    return currentPlan.type === PlanType.pro;
  }, [currentPlan]);

  const totalCredits = currentPlan ? (currentPlan.details.credits || 0) + (extraCredits || 0) : 0;

  return useMemo(
    () => ({
      plans,
      changePackage,
      currentPlanId,
      currentPlan,
      isLoading,
      isBillingLoading: isBillingPending || isBillingLoading || isLoadingUser,
      isBillingError,
      creditsRefreshedAt: billingData?.creditsRefreshedAt || null,
      remainingCredits: calculateRemainingCredits({
        subscriptionCredits,
        extraCredits,
      }),
      totalCredits,
      isProPlan,
    }),
    [
      billingData,
      isBillingLoading,
      isBillingPending,
      isBillingError,
      currentPlanId,
      isLoading,
      isLoadingUser,
      subscriptionCredits,
      extraCredits,
      changePackage,
    ]
  );
};

export const useBillingPrefetch = () => {
  const { appKey, isLoggedIn } = useCurrentApp();
  const queryClient = useQueryClient();
  const prefetchBilling = useCallback(() => {
    if (appKey && isLoggedIn) {
      queryClient.prefetchQuery({
        queryKey: ['billing', appKey],
        queryFn: () => fetchPlans({ appKey }),
        staleTime: 5 * 60 * 1000,
      });
    }
  }, [appKey, isLoggedIn, queryClient]);

  useEffect(() => {
    prefetchBilling();
  }, [prefetchBilling]);

  return prefetchBilling;
};

export default useBilling;
